/**
 * <CancelReasonPage> — étape 1/5 : pourquoi ? (page)
 *
 * Une question, une liste de motifs, et une précision écrite quand le motif la demande. Aucun
 * appel de service à cette étape : c'est la seule du parcours qui ne coûte rien.
 *
 * Contrôlée de bout en bout — le motif choisi et la précision arrivent en props et remontent par
 * rappels. La seule chose que la page dérive, c'est l'allumage de « Continuer », et elle le
 * demande au formatter.
 */
import React from "react"

import { type UICancelReason } from "../../../ports/primary/ui/types"
import { canSubmitCancelReason } from "../../../ports/primary/ui/formatters/subscriptionCancelFormatter"
import { Button } from "../../atoms/Buttons/Button"
import { TextInput } from "../../atoms/TextInput"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { OptionCard } from "../../molecules/OptionCard"
import { CancelFlowStep } from "../../templates/CancelFlowStep"

const STEP_TITLE = "Avant tout, pouvez-vous nous dire pourquoi ?"
const LEAD = "Cela nous aide à améliorer Masteur. Une seule réponse."
const DETAIL_PLACEHOLDER = "Dites-nous en plus… (requis)"
const CONTINUE_LABEL = "Continuer"

/** Hauteur du champ libre — les quatre lignes du legacy. */
const DETAIL_LINES = 4

export type CancelReasonPageProps = {
    /** Les motifs proposés, dans l'ordre. */
    reasons: UICancelReason[]
    /** Le motif choisi, ou `null`. */
    selectedReason: string | null
    onSelectReason: (key: string) => void
    /** La précision écrite — vide tant que rien n'est saisi. */
    detail: string
    onDetailChange: (text: string) => void
    onContinue: () => void
    onBack?: () => void
    onClose?: () => void
    testID?: string
}

export function CancelReasonPage(props: CancelReasonPageProps) {
    const { reasons, selectedReason, onSelectReason, detail, onDetailChange, onContinue, onBack, onClose, testID } =
        props

    const needsDetail = reasons.some((reason) => reason.key === selectedReason && reason.requiresDetail === true)

    return (
        <CancelFlowStep testID={testID} stepTitle={STEP_TITLE} step={1} onBack={onBack} onClose={onClose}>
            <Stack gap="4">
                <Text size="text-base" color="text-muted">
                    {LEAD}
                </Text>

                <Stack gap="2">
                    {reasons.map((reason) => (
                        <OptionCard
                            key={reason.key}
                            label={reason.label}
                            selected={selectedReason === reason.key}
                            onPress={() => onSelectReason(reason.key)}
                        />
                    ))}
                </Stack>

                {needsDetail && (
                    <TextInput
                        value={detail}
                        onChangeText={onDetailChange}
                        placeholder={DETAIL_PLACEHOLDER}
                        multiline
                        numberOfLines={DETAIL_LINES}
                    />
                )}

                <Button
                    label={CONTINUE_LABEL}
                    fullWidth
                    disabled={!canSubmitCancelReason(reasons, selectedReason, detail)}
                    onPress={onContinue}
                />
            </Stack>
        </CancelFlowStep>
    )
}
